// ============================================================
// reportFile.ts — Récupération/ouverture d'un fichier de rapport,
// indépendant du driver de stockage backend (local ou s3).
//
// L'endpoint GET /reportJob/file renvoie :
//   - driver s3    : JSON { url } (presigned URL) → on navigue vers cette URL.
//   - driver local : les octets du fichier → on construit un blob: URL.
//
// On détecte le cas via le content-type de la réponse (toujours lue en blob).
// Le front reste ainsi totalement agnostique du driver backend.
// ============================================================

import api from '../reduxStore/api';

export type DownloadMode = 'inline' | 'attachment';

interface OpenReportOptions {
  /** 'inline' → afficher/imprimer (nouvel onglet) ; 'attachment' → télécharger. */
  mode: DownloadMode;
  /** Nom de fichier proposé (sans extension) — utilisé pour le téléchargement. */
  fileName?: string;
  /** 'PDF' | 'EXCEL' — détermine l'extension du fichier téléchargé. */
  format: string;
}

const EXT: Record<string, string> = { PDF: 'pdf', EXCEL: 'xlsx' };

/** Déclenche le téléchargement d'une URL via une ancre invisible (pas de navigation de page). */
function triggerDownload(url: string, downloadName?: string): void {
  const a = document.createElement('a');
  a.href = url;
  if (downloadName) a.download = downloadName; // ignoré en cross-origin S3 → Content-Disposition prend le relais
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Récupère le fichier d'un rapport DONE et l'ouvre/télécharge selon `mode`.
 * Fonctionne identiquement quel que soit le driver de stockage backend.
 */
export async function openReportFile(jobId: number, opts: OpenReportOptions): Promise<void> {
  const { mode, format, fileName } = opts;
  const ext = EXT[format] ?? 'bin';

  const res = await api.get('/reportJob/file', {
    params:       { id: jobId, mode, filename: fileName },
    responseType: 'blob',
  });
  const blob = res.data as Blob;

  // ── Driver s3 : la réponse est un JSON { url } (presigned URL) ──
  if (blob.type.includes('application/json')) {
    const { url } = JSON.parse(await blob.text());
    if (mode === 'inline') {
      // Le PDF s'affiche dans l'onglet (Content-Disposition: inline côté S3).
      window.open(url, '_blank');
    } else {
      // S3 force le téléchargement via Content-Disposition: attachment.
      triggerDownload(url);
    }
    return;
  }

  // ── Driver local : la réponse est le fichier lui-même ──
  const url = URL.createObjectURL(blob);
  if (mode === 'inline') {
    window.open(url, '_blank');
    // Pas de revoke immédiat : l'onglet doit pouvoir charger le blob.
  } else {
    triggerDownload(url, `${fileName ?? 'rapport'}.${ext}`);
    URL.revokeObjectURL(url);
  }
}
